import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';
import { headerSearchStyles as s } from './HeaderSearch.styles';
import type { HeaderSearchProps } from './HeaderSearch.types';
import { HeaderSearchPanel } from './HeaderSearchPanel';
import { HeaderSearchInput } from './HeaderSearchInput';
import { useHeaderSearch } from './useHeaderSearch';
import { clearSearchHistory, readSearchHistory, removeSearchHistory } from './searchHistory';

export function HeaderSearch({ onFocusChange }: HeaderSearchProps) {
  const search = useHeaderSearch(onFocusChange);
  const {
    t,
    open,
    mobile,
    top,
    activeIndex,
    setActiveIndex,
    history,
    setHistory,
    rootRef,
    surfaceRef,
    panelRef,
    triggerRef,
    query,
    enabled,
    matchesQuery,
    feed,
    facets,
    categories,
    loading,
    items,
    closeSearch,
    navigateToSearch,
    finishNavigation,
    handleBlur,
    setOpen,
  } = search;
  const form = <HeaderSearchInput search={search} />;
  const panel = (
    <HeaderSearchPanel
      query={query}
      history={history}
      categories={categories}
      matches={enabled && matchesQuery ? (facets.data?.categories ?? []) : []}
      brands={
        enabled && matchesQuery
          ? (facets.data?.attributes.find((attribute) => attribute.code === 'brand')?.options ?? [])
          : []
      }
      products={items}
      collections={search.collections}
      collectionsLoading={search.collectionsLoading}
      collectionsFailed={matchesQuery && search.collectionFeed.isError}
      collectionsTotal={matchesQuery ? search.collectionFeed.data?.meta.total : undefined}
      collectionsUrl={search.collectionsUrl}
      mobile={mobile}
      resultSection={search.resultSection}
      onSelectSection={search.selectResultSection}
      onRetryCollections={() => {
        void search.collectionFeed.refetch();
      }}
      activeIndex={activeIndex}
      loading={loading}
      failed={matchesQuery && feed.isError}
      total={matchesQuery ? feed.data?.totalItems : undefined}
      panelRef={panelRef}
      onSelect={navigateToSearch}
      onNavigate={finishNavigation}
      onClearHistory={() => {
        clearSearchHistory();
        setHistory([]);
        setActiveIndex(-1);
      }}
      onRemoveHistory={(recent) => {
        removeSearchHistory(recent);
        setHistory((current) => current.filter((entry) => entry !== recent));
        setActiveIndex(-1);
      }}
      onRetry={() => {
        void feed.refetch();
        void facets.refetch();
      }}
    />
  );

  return (
    <div ref={rootRef} tabIndex={-1} className={s.root} onBlur={handleBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={s.iconButtonMobile}
        aria-label={t('nav.search')}
        aria-expanded={open}
        onClick={() => {
          setHistory(readSearchHistory());
          setOpen(true);
          onFocusChange?.(true);
        }}
      >
        <Search className={s.iconGlyph} aria-hidden />
      </button>
      {!(mobile && open) && form}
      {open &&
        createPortal(
          mobile ? (
            <div
              ref={surfaceRef}
              className={s.mobile}
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.search')}
              onBlur={handleBlur}
            >
              <div className={s.mobileHeader}>{form}</div>
              {panel}
            </div>
          ) : (
            <>
              <div className={s.scrim} style={{ top }} aria-hidden onClick={closeSearch} />
              <div
                ref={surfaceRef}
                className={s.desktopPanel}
                style={{
                  top,
                  height: `min(${query.length < 2 ? '30rem' : '44rem'}, 72dvh, calc(100dvh - ${top + 16}px))`,
                }}
                onBlur={handleBlur}
              >
                {panel}
              </div>
            </>
          ),
          document.body,
        )}
    </div>
  );
}
